import React, { useState, useRef } from 'react';
import api from '../../services/api';

function formatarData(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function IconeArquivo() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function CardAnexo({ tipo, anexoEnviado, onboardingId, readOnly, atualizarDados }) {
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState('');
  const inputRef = useRef(null);

  async function handleUpload() {
    if (!arquivo) return;
    setEnviando(true);
    setErro('');
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append('tipoId', tipo.id);
      formData.append('label', tipo.label);
      if (tipo.temSenha && senha) {
        formData.append('senha', senha);
      }
      await api.post(`/uploads/${onboardingId}/anexos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await atualizarDados();
      setArquivo(null);
      setSenha('');
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar o arquivo. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  async function handleBaixar() {
    try {
      const resp = await api.get(
        `/uploads/${onboardingId}/anexos/${anexoEnviado.id}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = anexoEnviado.nomeArquivo || tipo.label;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      // falha silenciosa no download
    }
  }

  async function handleExcluir() {
    if (!window.confirm(`Deseja excluir o arquivo "${anexoEnviado.nomeArquivo || tipo.label}"? Esta ação não pode ser desfeita.`)) return;
    setExcluindo(true);
    setErro('');
    try {
      await api.delete(`/uploads/${onboardingId}/${anexoEnviado.id}`);
      await atualizarDados();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao excluir. Tente novamente.');
    } finally {
      setExcluindo(false);
    }
  }

  const enviado = !!anexoEnviado;

  return (
    <div className="card space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            enviado ? 'bg-green-100 text-green-600' : 'bg-amber-50 text-amber-500'
          }`}>
            <IconeArquivo />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm">{tipo.label}</p>
            {tipo.descricao && (
              <p className="text-xs text-gray-400 mt-0.5">{tipo.descricao}</p>
            )}
          </div>
        </div>

        {enviado ? (
          <span className="badge-ok shrink-0">Enviado</span>
        ) : (
          <span className="badge-pending shrink-0">Pendente</span>
        )}
      </div>

      {/* Arquivo enviado */}
      {enviado && (
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-green-50 border border-green-100 rounded-lg">
          <div className="min-w-0">
            <p className="text-sm font-medium text-green-800 truncate">
              {anexoEnviado.nomeArquivo || 'Arquivo enviado'}
            </p>
            {anexoEnviado.criadoEm && (
              <p className="text-xs text-green-600 mt-0.5">
                Enviado em {formatarData(anexoEnviado.criadoEm)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleBaixar}
              className="btn-secondary text-xs px-3 py-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Baixar
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={handleExcluir}
                disabled={excluindo}
                className="btn-danger text-xs px-3 py-1.5"
              >
                {excluindo ? 'Excluindo...' : 'Excluir'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Área de upload (somente quando pendente e não finalizado) */}
      {!enviado && !readOnly && (
        <div className="space-y-3">
          {/* Senha do certificado */}
          {tipo.temSenha && (
            <div>
              <label className="label">Senha do certificado</label>
              <input
                type="password"
                value={senha}
                onChange={e => setSenha(e.target.value)}
                className="input"
                placeholder="Informe a senha do certificado A1"
                autoComplete="new-password"
              />
            </div>
          )}

          {/* Input de arquivo estilizado */}
          <div>
            <label
              htmlFor={`upload-${tipo.id}`}
              className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
                arquivo
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-gray-200 hover:border-brand-300 hover:bg-brand-50'
              }`}
            >
              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {arquivo ? (
                <span className="text-sm font-medium text-brand-700 text-center break-all">
                  {arquivo.name}
                </span>
              ) : (
                <>
                  <span className="text-sm font-medium text-gray-600">Clique para selecionar o arquivo</span>
                  <span className="text-xs text-gray-400">ou arraste e solte aqui</span>
                </>
              )}
            </label>
            <input
              id={`upload-${tipo.id}`}
              ref={inputRef}
              type="file"
              className="sr-only"
              onChange={e => setArquivo(e.target.files?.[0] || null)}
            />
          </div>

          {arquivo && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUpload}
                disabled={enviando}
                className="btn-primary"
              >
                {enviando ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Enviar arquivo
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Estado pendente + readonly */}
      {!enviado && readOnly && (
        <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
          <p className="text-xs text-amber-700">Documento não enviado</p>
        </div>
      )}

      {erro && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-700">{erro}</p>
        </div>
      )}
    </div>
  );
}

export default function Anexos({ dados, anexosPadrao, onboardingId, atualizarDados }) {
  const readOnly = !!dados.finalizado;
  const extras = (dados.tiposAnexosExtras || []).map(t => ({
    id: t.id || t.label,
    label: t.label,
    descricao: t.descricao || '',
    temSenha: false,
  }));

  const todosOsTipos = [...(anexosPadrao || []), ...extras];
  const anexosEnviados = dados.anexos || [];

  function encontrarAnexoEnviado(tipoId) {
    return anexosEnviados.find(a => a.tipoId === tipoId && a.arquivo) || null;
  }

  const totalEnviados = todosOsTipos.filter(t => encontrarAnexoEnviado(t.id)).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="section-title">Anexos</h1>
        <p className="section-sub">
          Envie os documentos necessários para o seu onboarding.
        </p>
      </div>

      {todosOsTipos.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-100 rounded-lg">
          <div className="flex-1">
            <div className="flex justify-between text-xs font-medium text-brand-700 mb-1.5">
              <span>Documentos enviados</span>
              <span>{totalEnviados} de {todosOsTipos.length}</span>
            </div>
            <div className="w-full bg-brand-100 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full bg-brand-600 transition-all duration-500"
                style={{ width: todosOsTipos.length > 0 ? `${Math.round((totalEnviados / todosOsTipos.length) * 100)}%` : '0%' }}
              />
            </div>
          </div>
        </div>
      )}

      {todosOsTipos.length === 0 ? (
        <div className="card text-center py-12 space-y-3">
          <div className="text-4xl">📎</div>
          <p className="font-semibold text-gray-700">Nenhum documento necessário</p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Não há documentos configurados para envio neste onboarding.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {todosOsTipos.map(tipo => (
            <CardAnexo
              key={tipo.id}
              tipo={tipo}
              anexoEnviado={encontrarAnexoEnviado(tipo.id)}
              onboardingId={onboardingId}
              readOnly={readOnly}
              atualizarDados={atualizarDados}
            />
          ))}
        </div>
      )}
    </div>
  );
}
